import { useSocket } from "@/contexts/SocketContext.tsx";
import { useThrottledInvalidation } from "@/shared/useThrottledInvalidation.ts";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { api } from "@/lib/http.ts";
import type { CalendarEventQueryInput, CalendarEventResponse } from "@/types/events.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type {
  CalendarEventApplyFailedEventData,
  CalendarEventsChangedEventData,
} from "@/types/socketIo.ts";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { errorToast } from "@/components/ui/toast.tsx";
import { reminderChipEnd } from "@/shared/reminderChip.ts";
import { isReminderBlob } from "./canEditEvent.ts";
import { getDateRange } from "./dateRange.ts";
import {
  confirmPendingBufferMoves,
  getPendingBufferMoves,
  subscribePendingBufferMoves,
} from "./pendingBufferMoves.ts";
import { fullCalendarEventId } from "./fullCalendarEventId.ts";
import type { ViewType } from "./types.ts";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Fetches calendar events for the specified date range and view type.
 * Transforms events into FullCalendar format.
 *
 * @param calendarIds - Specific calendar IDs to fetch. If `undefined`, uses
 *   user preferences (availability calendars). Pass `null` to defer the query
 *   until calendar preferences are loaded (prevents a phantom empty-results
 *   fetch). Pass `[]` to short-circuit to zero events without hitting the
 *   network — used when the user has explicitly hidden every calendar, where
 *   omitting the param would silently fall back to the availability set.
 */
export const useCalendarEvents = (
  currentDate: Date,
  viewType: ViewType,
  calendarIds?: string[] | null,
  /** Only meaningful for "day" view. Used by the mobile 2-day grid. */
  dayCount = 1,
) => {
  const { getUserTimezone } = useCurrentUserContext();
  const timezone = useMemo(() => getUserTimezone(), [getUserTimezone]);

  const { start, end } = getDateRange(currentDate, viewType, dayCount);

  const calendarIdsParam = calendarIds?.join(",");
  const isExplicitEmpty = Array.isArray(calendarIds) && calendarIds.length === 0;

  const queryParams = useMemo(
    () =>
      ({
        timeMin: start.toISOString(),
        timeMax: end.toISOString(),
        userTimezone: timezone,
        ...(calendarIdsParam ? { calendarIds: calendarIdsParam } : {}),
      }) satisfies CalendarEventQueryInput,
    [start, end, timezone, calendarIdsParam],
  );

  const {
    data,
    error,
    isLoading: queryIsLoading,
    isPlaceholderData,
  } = useQuery<CalendarEventResponse>({
    queryKey: [
      "calendarEvents",
      viewType,
      start.toISOString(),
      end.toISOString(),
      calendarIdsParam,
      timezone,
    ],
    queryFn: () =>
      api
        .get<CalendarEventResponse>("/api/a/events/daily_agenda", { params: queryParams })
        .then((res) => res.data),
    enabled: calendarIds !== null && !isExplicitEmpty,
    refetchInterval: FIVE_MINUTES_MS,
    staleTime: FIVE_MINUTES_MS,
    placeholderData: (previousData) => previousData,
    // Don't retry into a rate limit or an overloaded backend — retrying a 429
    // or 5xx during a refetch burst only deepens the storm (BIG-5628). The
    // throttled socket invalidation will refetch again on the next window.
    retry: (failureCount, error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 429 || (status !== undefined && status >= 500)) return false;
      return failureCount < 1;
    },
  });

  // Treat null calendarIds (calendars not yet loaded) as loading so callers show a spinner.
  const isLoading = calendarIds === null || (!isExplicitEmpty && queryIsLoading);

  // The window `events` actually covers, or null while it doesn't cover this one
  // yet. `placeholderData` keeps the *previous* window's events on screen during a
  // navigation, so "not loading" isn't enough to conclude the current window has
  // been fetched — callers that need to distinguish "no events here" from "never
  // looked here" (selection match reconciliation) key off this.
  //
  // Null while every calendar is hidden too: the query is short-circuited, so the
  // window was never actually looked at and treating it as loaded would resolve
  // ranges to "no matching events" that unhiding could never revisit.
  const startISO = start.toISOString();
  const endISO = end.toISOString();
  const loadedRange = useMemo(
    () =>
      data && !isPlaceholderData ? { start: new Date(startISO), end: new Date(endISO) } : null,
    [data, isPlaceholderData, startISO, endISO],
  );

  // Optimistic travel-buffer positions, kept in sync via useSyncExternalStore.
  // useEventDrag writes here so a dragged meeting's buffers move in the same
  // frame; we overlay them below until the cascade-confirmed data arrives.
  const pendingBufferMoves = useSyncExternalStore(
    subscribePendingBufferMoves,
    getPendingBufferMoves,
  );

  // Retire optimistic moves the server has caught up to (or that expired). Runs
  // after each fresh fetch; a no-op when nothing matches, so it won't loop.
  useEffect(() => {
    if (!data?.events) return;
    confirmPendingBufferMoves(
      data.events
        .filter((e) => e.externalEventId)
        .map((e) => ({
          externalEventId: e.externalEventId as string,
          startMs: Date.parse(e.start.dateTime),
          endMs: Date.parse(e.end.dateTime),
        })),
    );
  }, [data]);

  // Transform to FullCalendar event format
  const events = useMemo(() => {
    if (isExplicitEmpty) return [];
    if (!data?.events) return [];
    return data.events.map((event) => {
      const pending = event.externalEventId
        ? pendingBufferMoves.get(event.externalEventId)
        : undefined;
      const startIso = pending ? new Date(pending.startMs).toISOString() : event.start.dateTime;

      // A reminder is stored as a moment, which the grid has no way to lay out.
      // Give it a half-hour to draw over — presentation only, the same way the
      // "Reminder: " prefix is. `buildMoveDates` writes `end === start` back on
      // a drag, so the end below never reaches the server.
      let endIso: string;
      if (isReminderBlob(event.okthx)) {
        endIso = reminderChipEnd(new Date(startIso)).toISOString();
      } else if (pending) {
        endIso = new Date(pending.endMs).toISOString();
      } else {
        endIso = event.end.dateTime;
      }

      return {
        id: fullCalendarEventId(event),
        title: event.summary,
        start: startIso,
        end: endIso,
        allDay: event.isAllDay ?? false,
        extendedProps: {
          // The source ent id (stringified) — the canonical handle for opening,
          // resizing, or otherwise acting on the event. Kept here because `id`
          // above is now a stable composite for recurring instances.
          gcalId: event.gcalId,
          instanceKey: event.instanceKey,
          calendarId: event.calendarId,
          colorId: event.colorId,
          htmlLink: event.htmlLink,
          description: event.description,
          isPast: event.isPast,
          formattedRange: event.formattedRange,
          isAllDay: event.isAllDay,
          location: event.location,
          status: event.status,
          participants: event.participants,
          pendingSyncUpdate: event.pendingSyncUpdate,
          // Consumed by the drag-to-reschedule flow (useEventDrag, canDragEvent,
          // EventRescheduleConfirmDialog). Not strictly needed by render code,
          // but FullCalendar's EventApi only surfaces extendedProps to the drag
          // callbacks — so the drag predicate has to read everything from here.
          externalEventId: event.externalEventId,
          recurringEventId: event.recurringEventId,
          isRecurring: event.isRecurring,
          guestsCanModify: event.guestsCanModify,
          // Buffer holds of this primary, so the drag flow can move them too.
          bufferExternalEventIds: event.bufferExternalEventIds,
          // Present only on okthx events (a reminder today). Both the chip
          // renderer and the drag predicate branch on it, and FullCalendar only
          // surfaces extendedProps to the drag callbacks.
          okthx: event.okthx,
          // The brief Okthx has written about this meeting, so the chip marks
          // itself. An id only; the brief is fetched when it is opened.
          briefId: event.briefId,
        },
      };
    });
  }, [data, isExplicitEmpty, pendingBufferMoves]);

  return { events, error, isLoading, loadedRange };
};

/**
 * Global socket listener that invalidates calendar event queries when the server
 * signals that event sources have changed. Mount once at the app level.
 *
 * Follows the same pattern as useThreadMutatedSocket — invalidation only triggers
 * a refetch if the query is actively observed.
 * @see https://tkdodo.eu/blog/using-web-sockets-with-react-query
 */
export const useCalendarEventsSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  // Calendars changed since the last invalidation. The throttle *timing* lives
  // in the shared hook below; this ref is the calendar-specific state it flushes.
  const pendingCalendarIds = useRef<Set<string>>(new Set());

  const flush = useCallback(() => {
    const changed = pendingCalendarIds.current;
    pendingCalendarIds.current = new Set();
    // Defensive: the handler always adds an id before triggering and nothing
    // else drains this set, so an empty flush shouldn't happen. If one ever does,
    // report a no-op rather than stamping the throttle's quiet-window clock for a
    // flush that refetched nothing, which would suppress the next burst refill.
    if (changed.size === 0) return false;

    // Refetch at most one query per distinct visible date-range. The frequency
    // throttle above bounds how often we flush; this bounds the per-flush
    // fan-out. Multiple mounted observers (or calendar-filter variants) over the
    // same range would otherwise each fire a refetch on every flush, multiplying
    // load on the server rate limiter (BIG-5787). All calendar views are driven
    // by one shared selected-calendar set, so one refetch per date-range covers
    // them. invalidateQueries only refetches actively-observed queries, so this
    // keeps the "refetch only if mounted" behavior.
    const seenRanges = new Set<string>();
    // Prefer actively-observed queries: when two calendar-filter variants share a
    // date-range, the dedup keeps the first one seen, so sort observed queries
    // ahead of stale/unmounted ones. Otherwise the dedup could pick a stale
    // cached query and leave the mounted view unrefreshed until the next poll.
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["calendarEvents"] })
      .sort((a, b) => b.getObserversCount() - a.getObserversCount());
    for (const query of queries) {
      // queryKey: ["calendarEvents", viewType, startISO, endISO, calendarIdsParam, timezone]
      const key = query.queryKey;
      const calendarIdsParam = key[4] as string | undefined;
      // No calendarIds filter = uses default availability calendars, invalidate to be safe.
      const matches =
        !calendarIdsParam || calendarIdsParam.split(",").some((id) => changed.has(id));
      if (!matches) continue;
      const rangeKey = `${String(key[1])}|${String(key[2])}|${String(key[3])}`;
      if (seenRanges.has(rangeKey)) continue;
      seenRanges.add(rangeKey);
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
    }

    // A source's instanceKey embeds its start time, so an externally rescheduled
    // event changes it. The spotlight matches that key against the agenda's, so
    // both sides have to refetch together; only mounted source queries refetch,
    // so this costs at most one request.
    void queryClient.invalidateQueries({ queryKey: ["source"] });

    // A deep-linked detail view is fed by the `["event", id]` detail query
    // rather than the grid cache, so it has to be invalidated too or it never
    // converges on an external edit (BIG-6330). Only mounted detail views
    // refetch, so this costs at most one request per open detail view.
    void queryClient.invalidateQueries({ queryKey: ["event"] });
  }, [queryClient]);

  // Burst + trailing throttle at the shared defaults: refetch immediately for the
  // first few emissions (so single creates and small flurries feel instant), then
  // collapse a long sync into one invalidation per window.
  //
  // Unlike the server's per-`accountId:calendarId` allowance, this budget is
  // global across calendars — the calendar query is shared, so a flurry on one
  // calendar spends the same budget as another. That's intentional: the goal is
  // bounding total client refetches, not per-calendar fairness.
  const { trigger, cancelPending } = useThrottledInvalidation(flush);

  const handleCalendarEventsChanged = useCallback(
    (data: CalendarEventsChangedEventData) => {
      pendingCalendarIds.current.add(data.calendarId);
      trigger();
    },
    [trigger],
  );

  // A calendar edit the server accepted optimistically could not be applied to
  // the provider (BIG-7535). The rollback's own `calendar:events_changed` emit
  // handles the refetch; this listener's job is the acknowledgement — without
  // it the grid silently snaps back and the user is left wondering whether
  // their change took.
  const handleCalendarEventApplyFailed = useCallback((data: CalendarEventApplyFailedEventData) => {
    const eventLabel = data.summary ? `"${data.summary}"` : "your event";
    errorToast(`Couldn't update ${eventLabel}`, undefined, {
      description: data.reverted
        ? "The change didn't reach your calendar provider, so it was rolled back. Please try again."
        : "The change didn't reach your calendar provider. Please try again.",
    });
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Drain anything the cleanup below parked. Waiting for the next event to
    // carry them would strand the set whenever the sync that produced the burst
    // finished during the outage — the common case, since the outage is what
    // made this tab miss the tail.
    if (pendingCalendarIds.current.size > 0) trigger();

    socket.on("calendar:events_changed", handleCalendarEventsChanged);
    socket.on("calendar:event_apply_failed", handleCalendarEventApplyFailed);
    return () => {
      socket.off("calendar:events_changed", handleCalendarEventsChanged);
      socket.off("calendar:event_apply_failed", handleCalendarEventApplyFailed);
      // A disconnect nulls the socket without unmounting this hook, so the
      // throttle's unmount cleanup never runs. Drop only the scheduled flush —
      // `pendingCalendarIds` is untouched, so those calendars ride the reconnect
      // above rather than being lost. Letting it land instead would stamp the
      // throttle window mid-outage, pushing the first post-reconnect refetch a
      // window later, which is exactly when the data is most likely to be stale.
      cancelPending();
    };
  }, [socket, handleCalendarEventsChanged, handleCalendarEventApplyFailed, cancelPending, trigger]);
};
